import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { PageHero, StatCard } from 'tio-ui/components/patterns';
import {
  PhMagnifyingGlass,
  PhGraduationCap,
  PhBuildings,
  PhFlag,
  PhBriefcase,
  PhArrowRight,
  PhCheckCircle,
  PhWarning,
  PhQuestion,
  PhLightning,
  PhCurrencyDollar,
  PhHandshake,
  PhBank,
  PhCaretRight,
} from 'ember-phosphor-icons';
import type { LrapDiscoverySummary, LrapProgram } from 'tio-employee/services/career-pathways-api';

interface LrapIndexModel {
  discovery: LrapDiscoverySummary;
  programs: LrapProgram[];
}

interface LrapIndexSignature {
  Args: {
    model: LrapIndexModel;
  };
}

export default class CareerPathwaysLrapIndex extends Component<LrapIndexSignature> {
  @tracked activeTab: 'enrolled' | 'eligible' | 'all' = 'eligible';
  @tracked expandedProgramId: string | null = null;

  get discovery() {
    return this.args.model.discovery;
  }

  get programs() {
    return this.args.model.programs;
  }

  get hasCompletedDiscovery() {
    return this.discovery.hasCompletedDiscovery;
  }

  get enrolledPrograms() {
    return this.discovery.enrolledPrograms;
  }

  get eligiblePrograms() {
    return this.discovery.eligiblePrograms;
  }

  get hasOutsideFundsConflicts() {
    return this.discovery.outsideFundsConflicts.length > 0;
  }

  get stackingOptimization() {
    return this.discovery.stackingOptimization;
  }

  formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  getTypeIcon = (type: string) => {
    switch (type) {
      case 'school':
        return PhGraduationCap;
      case 'state':
        return PhFlag;
      case 'federal':
        return PhBuildings;
      case 'employer':
        return PhBriefcase;
      default:
        return PhHandshake;
    }
  };

  getStatusColor = (status: string) => {
    switch (status) {
      case 'eligible':
        return 'success';
      case 'likely-eligible':
        return 'primary';
      case 'check-required':
        return 'warning';
      case 'ineligible':
        return 'danger';
      default:
        return 'default';
    }
  };

  toggleProgram = (programId: string) => {
    this.expandedProgramId = this.expandedProgramId === programId ? null : programId;
  };

  setTab = (tab: 'enrolled' | 'eligible' | 'all') => {
    this.activeTab = tab;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
      {{! Hero Section }}
      <PageHero @theme="teal" @rounded="2xl">
        <:icon>
          <PhMagnifyingGlass @size={{32}} />
          <Heading @level={{1}} class="text-white text-2xl md:text-3xl">
            LRAP Discovery
          </Heading>
        </:icon>
        <:default>
          <Text class="text-teal-100 text-lg mb-6 max-w-2xl">
            Discover loan repayment assistance programs from schools, states, and federal agencies.
            Find money you didn't know existed.
          </Text>

          {{#if this.hasCompletedDiscovery}}
            <div class="bg-white/10 backdrop-blur rounded-xl p-4 max-w-lg">
              <div class="flex items-center justify-between">
                <div>
                  <Text class="text-teal-100 text-sm">Total Potential Annual Value</Text>
                  <Text class="text-2xl font-bold text-white">
                    {{this.formatCurrency this.discovery.totalPotentialAnnualValue}}
                  </Text>
                </div>
                <div class="text-right">
                  <Text class="text-teal-100 text-sm">Programs Found</Text>
                  <Text class="text-2xl font-bold text-white">
                    {{add this.enrolledPrograms.length this.eligiblePrograms.length}}
                  </Text>
                </div>
              </div>
            </div>
          {{else}}
            <Button
              @intent="primary"
              @icon={{PhArrowRight}}
              @iconPlacement="end"
              @label="Start Discovery"
              class="bg-white text-teal-700 hover:bg-teal-50"
            />
          {{/if}}
        </:default>
      </PageHero>

      {{! Quick Stats }}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhGraduationCap}}
          @value="{{this.enrolledPrograms.length}}"
          @label="Enrolled Programs"
          @iconClass="text-teal-600"
        />
        <StatCard
          @icon={{PhLightning}}
          @value="{{this.eligiblePrograms.length}}"
          @label="Eligible Programs"
          @trend="up"
          @trendText="Found for you"
          @iconClass="text-violet-600"
        />
        <StatCard
          @icon={{PhCurrencyDollar}}
          @value={{this.formatCurrency this.discovery.totalPotentialAnnualValue}}
          @label="Potential Annual"
          @iconClass="text-emerald-600"
        />
        <StatCard
          @icon={{PhWarning}}
          @value="{{this.discovery.outsideFundsConflicts.length}}"
          @label="Conflicts Found"
          @iconClass="text-amber-600"
        />
      </div>

      {{! Outside Funds Conflicts Warning }}
      {{#if this.hasOutsideFundsConflicts}}
        <div class="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div class="flex items-start gap-3">
            <PhWarning @size={{24}} class="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <Heading @level={{4}} class="text-amber-800 font-semibold mb-2">
                "Outside Funds" Conflicts Detected
              </Heading>
              <Text class="text-amber-700 text-sm mb-3">
                Some of your programs may reduce benefits when you receive employer contributions.
                We can help you optimize around these rules.
              </Text>
              {{#each this.discovery.outsideFundsConflicts as |conflict|}}
                <div class="bg-white/60 rounded-lg p-3 mb-2">
                  <Text class="font-medium text-amber-900">{{conflict.schoolProgram}}</Text>
                  <Text class="text-amber-700 text-sm">
                    {{conflict.recommendation}}
                  </Text>
                  <div class="flex items-center gap-4 mt-2 text-sm">
                    <span class="text-gray-600">
                      Employer contribution:
                      {{this.formatCurrency conflict.employerContribution}}
                    </span>
                    <span class="text-red-600">
                      Potential reduction:
                      {{this.formatCurrency conflict.potentialReduction}}
                    </span>
                  </div>
                </div>
              {{/each}}
              <LinkTo @route="authenticated.reimagined.finance.optimizer">
                <Button
                  @intent="primary"
                  @size="sm"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label="View Optimized Strategy"
                />
              </LinkTo>
            </div>
          </div>
        </div>
      {{/if}}

      {{! Stacking Optimization Panel }}
      {{#if this.stackingOptimization}}
        <div
          class="mt-6 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-6"
        >
          <div class="flex items-center gap-2 mb-4">
            <PhLightning @size={{24}} class="text-violet-600" />
            <Heading @level={{3}} class="text-lg font-semibold text-violet-900">
              Optimized Stacking Strategy
            </Heading>
          </div>

          <Text class="text-violet-700 mb-4">{{this.stackingOptimization.reasoning}}</Text>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div class="bg-white/60 rounded-lg p-3">
              <Text @intent="muted" @size="sm">Monthly Out-of-Pocket</Text>
              <Text class="text-xl font-bold text-violet-900">
                {{this.formatCurrency
                  this.stackingOptimization.projectedOutcome.outOfPocketMonthly
                }}
              </Text>
            </div>
            <div class="bg-white/60 rounded-lg p-3">
              <Text @intent="muted" @size="sm">Years to Payoff</Text>
              <Text class="text-xl font-bold text-violet-900">
                {{this.stackingOptimization.projectedOutcome.yearsToPayoff}}
              </Text>
            </div>
            <div class="bg-white/60 rounded-lg p-3">
              <Text @intent="muted" @size="sm">Total Forgiven</Text>
              <Text class="text-xl font-bold text-emerald-600">
                {{this.formatCurrency this.stackingOptimization.projectedOutcome.totalForgiven}}
              </Text>
            </div>
            <div class="bg-white/60 rounded-lg p-3">
              <Text @intent="muted" @size="sm">Retirement Added</Text>
              <Text class="text-xl font-bold text-teal-600">
                {{this.formatCurrency
                  this.stackingOptimization.projectedOutcome.retirementContribution
                }}
              </Text>
            </div>
          </div>

          <Heading @level={{4}} class="font-medium text-violet-800 mb-2">Recommended Allocation:</Heading>
          <div class="space-y-2">
            {{#each this.stackingOptimization.optimizedAllocation as |allocation|}}
              <div class="bg-white rounded-lg p-3 flex items-center gap-4">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center
                    {{if
                      (eq allocation.sourceType 'school')
                      'bg-violet-100'
                      (if
                        (eq allocation.sourceType 'employer')
                        'bg-teal-100'
                        (if (eq allocation.sourceType 'retirement') 'bg-emerald-100' 'bg-gray-100')
                      )
                    }}"
                >
                  {{#if (eq allocation.sourceType "school")}}
                    <PhGraduationCap @size={{20}} class="text-violet-600" />
                  {{else if (eq allocation.sourceType "employer")}}
                    <PhBriefcase @size={{20}} class="text-teal-600" />
                  {{else if (eq allocation.sourceType "retirement")}}
                    <PhBank @size={{20}} class="text-emerald-600" />
                  {{else}}
                    <PhHandshake @size={{20}} class="text-gray-600" />
                  {{/if}}
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <Text class="font-medium text-gray-900">{{allocation.source}}</Text>
                    <Text class="font-bold text-violet-700">
                      {{this.formatCurrency allocation.amount}}
                    </Text>
                  </div>
                  <Text @intent="muted" @size="sm">→ {{allocation.targetDebt}}</Text>
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{! Tab Navigation }}
      <div class="mt-8 border-b border-gray-200">
        <nav class="flex gap-6">
          <button
            type="button"
            class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
              {{if
                (eq this.activeTab 'enrolled')
                'border-teal-500 text-teal-600'
                'border-transparent text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" (fn this.setTab "enrolled")}}
          >
            Enrolled ({{this.enrolledPrograms.length}})
          </button>
          <button
            type="button"
            class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
              {{if
                (eq this.activeTab 'eligible')
                'border-teal-500 text-teal-600'
                'border-transparent text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" (fn this.setTab "eligible")}}
          >
            Eligible ({{this.eligiblePrograms.length}})
          </button>
          <button
            type="button"
            class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
              {{if
                (eq this.activeTab 'all')
                'border-teal-500 text-teal-600'
                'border-transparent text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" (fn this.setTab "all")}}
          >
            All Programs ({{this.programs.length}})
          </button>
        </nav>
      </div>

      {{! Program Lists }}
      <div class="mt-6 space-y-4">
        {{#if (eq this.activeTab "enrolled")}}
          {{#if this.enrolledPrograms.length}}
            {{#each this.enrolledPrograms as |enrollment|}}
              <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div class="flex items-start gap-4">
                  <div class="p-3 bg-emerald-100 rounded-xl">
                    {{#let (this.getTypeIcon enrollment.programType) as |Icon|}}
                      <Icon @size={{24}} class="text-emerald-600" />
                    {{/let}}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <Text class="font-semibold text-gray-900">{{enrollment.programName}}</Text>
                      <Chip @intent="success" @size="sm">Active</Chip>
                    </div>
                    <div class="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span>Annual: {{this.formatCurrency enrollment.annualBenefit}}</span>
                      <span>•</span>
                      <span>YTD: {{this.formatCurrency enrollment.ytdReceived}}</span>
                      <span>•</span>
                      <span>Lifetime: {{this.formatCurrency enrollment.lifetimeReceived}}</span>
                    </div>
                  </div>
                </div>
              </div>
            {{/each}}
          {{else}}
            <div class="text-center py-8">
              <PhGraduationCap @size={{48}} class="mx-auto text-gray-400 mb-4" />
              <Text class="text-gray-600">No programs enrolled yet. Check your eligible programs!</Text>
            </div>
          {{/if}}
        {{/if}}

        {{#if (eq this.activeTab "eligible")}}
          {{#if this.eligiblePrograms.length}}
            {{#each this.eligiblePrograms as |eligibility|}}
              <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  type="button"
                  class="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                  {{on "click" (fn this.toggleProgram eligibility.programId)}}
                >
                  <div class="flex items-start gap-4">
                    <div class="p-3 bg-violet-100 rounded-xl">
                      {{#let (this.getTypeIcon eligibility.program.type) as |Icon|}}
                        <Icon @size={{24}} class="text-violet-600" />
                      {{/let}}
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center justify-between">
                        <Text
                          class="font-semibold text-gray-900"
                        >{{eligibility.program.name}}</Text>
                        <div class="flex items-center gap-2">
                          <Chip @intent={{this.getStatusColor eligibility.status}} @size="sm">
                            {{eligibility.status}}
                          </Chip>
                          <Text class="font-bold text-emerald-600">
                            {{this.formatCurrency eligibility.estimatedAnnualValue}}/yr
                          </Text>
                        </div>
                      </div>
                      <Text @intent="muted" @size="sm" class="mt-1">
                        {{eligibility.program.description}}
                      </Text>
                    </div>
                    <PhCaretRight
                      @size={{20}}
                      class="text-gray-400 transition-transform
                        {{if (eq this.expandedProgramId eligibility.programId) 'rotate-90'}}"
                    />
                  </div>
                </button>

                {{#if (eq this.expandedProgramId eligibility.programId)}}
                  <div class="border-t border-gray-100 p-4 bg-gray-50">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Text class="font-medium text-gray-700 mb-2">Matched Criteria:</Text>
                        <ul class="space-y-1">
                          {{#each eligibility.matchedCriteria as |criteria|}}
                            <li class="flex items-center gap-2 text-sm text-emerald-700">
                              <PhCheckCircle @size={{16}} />
                              {{criteria}}
                            </li>
                          {{/each}}
                        </ul>
                      </div>
                      {{#if eligibility.missingCriteria.length}}
                        <div>
                          <Text class="font-medium text-gray-700 mb-2">Missing Criteria:</Text>
                          <ul class="space-y-1">
                            {{#each eligibility.missingCriteria as |criteria|}}
                              <li class="flex items-center gap-2 text-sm text-amber-700">
                                <PhQuestion @size={{16}} />
                                {{criteria}}
                              </li>
                            {{/each}}
                          </ul>
                        </div>
                      {{/if}}
                    </div>

                    {{#if eligibility.nextSteps.length}}
                      <div class="mt-4 pt-4 border-t border-gray-200">
                        <Text class="font-medium text-gray-700 mb-2">Next Steps:</Text>
                        <ul class="space-y-1">
                          {{#each eligibility.nextSteps as |step index|}}
                            <li class="flex items-center gap-2 text-sm text-gray-600">
                              <span
                                class="w-5 h-5 rounded-full bg-violet-100 text-violet-600 text-xs flex items-center justify-center font-medium"
                              >
                                {{add index 1}}
                              </span>
                              {{step}}
                            </li>
                          {{/each}}
                        </ul>
                      </div>
                    {{/if}}

                    <div class="mt-4 flex gap-2">
                      <a
                        href={{eligibility.program.applicationUrl}}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          @intent="primary"
                          @size="sm"
                          @icon={{PhArrowRight}}
                          @iconPlacement="end"
                          @label="Apply Now"
                        />
                      </a>
                      <Button @intent="default" @size="sm" @appearance="outlined">
                        Save for Later
                      </Button>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{/each}}
          {{else}}
            <div class="text-center py-8">
              <PhMagnifyingGlass @size={{48}} class="mx-auto text-gray-400 mb-4" />
              <Text class="text-gray-600">Complete the discovery questionnaire to find eligible
                programs.</Text>
              <Button @intent="primary" class="mt-4">
                Start Discovery
              </Button>
            </div>
          {{/if}}
        {{/if}}

        {{#if (eq this.activeTab "all")}}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            {{#each this.programs as |program|}}
              <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div class="flex items-start gap-3">
                  <div class="p-2 bg-gray-100 rounded-lg">
                    {{#let (this.getTypeIcon program.type) as |Icon|}}
                      <Icon @size={{20}} class="text-gray-600" />
                    {{/let}}
                  </div>
                  <div class="flex-1">
                    <Text class="font-medium text-gray-900">{{program.name}}</Text>
                    <Text @intent="muted" @size="sm">{{program.sponsor}}</Text>
                    <div class="flex items-center gap-2 mt-2">
                      <Chip @intent="default" @size="sm">{{program.type}}</Chip>
                      <Text class="text-sm text-emerald-600 font-medium">
                        Up to
                        {{this.formatCurrency program.maxAnnual}}/yr
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            {{/each}}
          </div>
        {{/if}}
      </div>

      {{! CTA }}
      <div class="mt-8 flex justify-center gap-4">
        <LinkTo @route="authenticated.reimagined.finance.optimizer">
          <Button @intent="default" @appearance="outlined" @label="View Section 127 Optimizer" />
        </LinkTo>
        <LinkTo @route="authenticated.reimagined.pslf-vault">
          <Button
            @intent="primary"
            @icon={{PhArrowRight}}
            @iconPlacement="end"
            @label="PSLF Compliance Vault"
          />
        </LinkTo>
      </div>
    </VStack>
  </template>
}

// Helper functions
function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

function add(a: number, b: number): number {
  return a + b;
}
